// ============================================
// PRICING MODEL
// Franchise-specific pricing rules and VAT
// ============================================

// ============================================
// PRICE LIST SCHEMA
// Supports different prices per franchise/outlet
// ============================================

export const PriceListSchema = {
  id: 'string',
  name: 'string',
  description: 'string',
  
  // Scope
  scope: {
    type: 'string',              // global, franchise, outlet
    franchiseeId: 'string',      // null for global
    outletId: 'string',          // null for franchise/global
  },
  
  // Currency
  currency: 'string',
  
  // Effective dates
  effectiveFrom: 'date',
  effectiveTo: 'date',
  
  // Prices (by menu item ID)
  prices: {
    // itemId: { price: number, overrideVatCategory: string }
  },
  
  // Status
  isActive: 'boolean',
  isDefault: 'boolean',          // Default for scope
  
  // Metadata
  createdAt: 'datetime',
  updatedAt: 'datetime',
  createdBy: 'string',
};

// ============================================
// VAT RULE SCHEMA
// Configurable per country/outlet/order type/category
// ============================================

export const VatRuleSchema = {
  id: 'string',
  name: 'string',
  
  // Scope - from most specific to least
  scope: {
    countryCode: 'string',       // Required
    outletId: 'string',          // Optional - outlet-specific override
    franchiseeId: 'string',      // Optional - franchisee override
  },
  
  // Rule conditions
  conditions: {
    orderType: 'string',         // dineIn, takeaway, delivery, all
    itemCategory: 'string',      // food, beverages, alcohol, merchandise, all
  },
  
  // VAT rate
  rate: 'number',                // Decimal (e.g., 0.25 for 25%)
  vatLabel: 'string',            // MVA, MOMS, VAT, etc.
  
  // Priority (higher = more specific, used first)
  priority: 'number',
  
  // Status
  isActive: 'boolean',
  effectiveFrom: 'date',
  effectiveTo: 'date',
  
  // Metadata
  createdAt: 'datetime',
  updatedAt: 'datetime',
};

// ============================================
// DEFAULT VAT RULES (from config)
// ============================================

export const defaultVatRules = [
  // Norway - Dine-in
  { id: 'no-dinein-food', countryCode: 'NO', orderType: 'dineIn', category: 'food', rate: 0.25, priority: 10 },
  { id: 'no-dinein-bev', countryCode: 'NO', orderType: 'dineIn', category: 'beverages', rate: 0.25, priority: 10 },
  { id: 'no-dinein-alc', countryCode: 'NO', orderType: 'dineIn', category: 'alcohol', rate: 0.25, priority: 10 },
  
  // Norway - Takeaway (reduced rate for food)
  { id: 'no-takeaway-food', countryCode: 'NO', orderType: 'takeaway', category: 'food', rate: 0.15, priority: 10 },
  { id: 'no-takeaway-bev', countryCode: 'NO', orderType: 'takeaway', category: 'beverages', rate: 0.25, priority: 10 },
  { id: 'no-takeaway-alc', countryCode: 'NO', orderType: 'takeaway', category: 'alcohol', rate: 0.25, priority: 10 },
  
  // Norway - Delivery (reduced rate for food)
  { id: 'no-delivery-food', countryCode: 'NO', orderType: 'delivery', category: 'food', rate: 0.15, priority: 10 },
  { id: 'no-delivery-bev', countryCode: 'NO', orderType: 'delivery', category: 'beverages', rate: 0.25, priority: 10 },
  { id: 'no-delivery-alc', countryCode: 'NO', orderType: 'delivery', category: 'alcohol', rate: 0.25, priority: 10 },
  
  // Sweden - All order types
  { id: 'se-all-food', countryCode: 'SE', orderType: 'all', category: 'food', rate: 0.12, priority: 5 },
  { id: 'se-all-bev', countryCode: 'SE', orderType: 'all', category: 'beverages', rate: 0.12, priority: 5 },
  { id: 'se-all-alc', countryCode: 'SE', orderType: 'all', category: 'alcohol', rate: 0.25, priority: 5 },
];

// ============================================
// PRICING HELPER FUNCTIONS
// ============================================

/**
 * Get effective price for an item
 * Checks: outlet -> franchisee -> global price lists
 */
export function getEffectivePrice(itemId, context) {
  const { outletId, franchiseeId, currency, priceLists } = context;
  
  // 1. Check outlet-specific price
  const outletPriceList = priceLists.find(
    pl => pl.isActive && pl.scope.outletId === outletId && pl.currency === currency
  );
  if (outletPriceList?.prices[itemId]) {
    return {
      price: outletPriceList.prices[itemId].price,
      source: 'outlet',
      priceListId: outletPriceList.id,
    };
  }
  
  // 2. Check franchisee price
  const franchiseePriceList = priceLists.find(
    pl => pl.isActive && 
    pl.scope.type === 'franchise' && 
    pl.scope.franchiseeId === franchiseeId && 
    pl.currency === currency
  );
  if (franchiseePriceList?.prices[itemId]) {
    return {
      price: franchiseePriceList.prices[itemId].price,
      source: 'franchise',
      priceListId: franchiseePriceList.id,
    };
  }
  
  // 3. Check global price
  const globalPriceList = priceLists.find(
    pl => pl.isActive && pl.scope.type === 'global' && pl.currency === currency
  );
  if (globalPriceList?.prices[itemId]) {
    return {
      price: globalPriceList.prices[itemId].price,
      source: 'global',
      priceListId: globalPriceList.id,
    };
  }
  
  return null;
}

/**
 * Get effective VAT rate for an item
 * Checks rules in priority order
 */
export function getEffectiveVatRate(context, vatRules = defaultVatRules) {
  const { countryCode, outletId, franchiseeId, orderType, itemCategory } = context;
  
  // Filter applicable rules and sort by priority (desc)
  const applicableRules = vatRules
    .filter(rule => {
      // Country must match
      if (rule.countryCode !== countryCode) return false;
      
      // Order type must match or be 'all'
      if (rule.orderType !== orderType && rule.orderType !== 'all') return false;
      
      // Category must match or be 'all'
      if (rule.category !== itemCategory && rule.category !== 'all') return false;
      
      // Check scope specificity
      if (rule.outletId && rule.outletId !== outletId) return false;
      if (rule.franchiseeId && rule.franchiseeId !== franchiseeId) return false;
      
      return true;
    })
    .sort((a, b) => {
      // Sort by specificity: outlet > franchise > country
      const aScore = (a.outletId ? 100 : 0) + (a.franchiseeId ? 50 : 0) + a.priority;
      const bScore = (b.outletId ? 100 : 0) + (b.franchiseeId ? 50 : 0) + b.priority;
      return bScore - aScore;
    });
  
  // Return first matching rule (highest priority)
  if (applicableRules.length > 0) {
    return {
      rate: applicableRules[0].rate,
      ruleId: applicableRules[0].id,
      source: applicableRules[0].outletId ? 'outlet' : 
              applicableRules[0].franchiseeId ? 'franchise' : 'country',
    };
  }
  
  // Fallback
  return { rate: 0.25, ruleId: null, source: 'fallback' };
}

/**
 * Calculate line item with VAT
 */
export function calculateLineItem(item, quantity, context) {
  const priceInfo = getEffectivePrice(item.id, context);
  const vatInfo = getEffectiveVatRate({
    ...context,
    itemCategory: item.vatCategory,
  });
  
  if (!priceInfo) {
    throw new Error(`No price found for item ${item.id}`);
  }
  
  const netAmount = priceInfo.price * quantity;
  const vatAmount = Math.round(netAmount * vatInfo.rate);
  const grossAmount = netAmount + vatAmount;
  
  return {
    itemId: item.id,
    itemName: item.name,
    quantity,
    unitPrice: priceInfo.price,
    priceSource: priceInfo.source,
    netAmount,
    vatRate: vatInfo.rate,
    vatRuleId: vatInfo.ruleId,
    vatAmount,
    grossAmount,
  };
}

/**
 * Calculate order totals with VAT breakdown
 */
export function calculateOrderTotals(lineItems) {
  const totals = {
    netTotal: 0,
    vatTotal: 0,
    grossTotal: 0,
    vatBreakdown: {},
  };
  
  lineItems.forEach(line => {
    totals.netTotal += line.netAmount;
    totals.vatTotal += line.vatAmount;
    totals.grossTotal += line.grossAmount;
    
    // Group by VAT rate
    const rateKey = `${line.vatRate * 100}%`;
    if (!totals.vatBreakdown[rateKey]) {
      totals.vatBreakdown[rateKey] = { netAmount: 0, vatAmount: 0 };
    }
    totals.vatBreakdown[rateKey].netAmount += line.netAmount;
    totals.vatBreakdown[rateKey].vatAmount += line.vatAmount;
  });
  
  return totals;
}

// ============================================
// PRICING RULE SCHEMA
// For franchise-specific pricing modifiers
// ============================================

export const PricingRuleSchema = {
  id: 'string',
  name: 'string',
  description: 'string',
  
  // Scope
  scope: {
    type: 'string',              // global, franchise, outlet
    franchiseeId: 'string',
    outletId: 'string',
  },
  
  // Rule type
  ruleType: 'string',            // percentage_modifier, fixed_modifier, set_price
  
  // Conditions
  conditions: {
    categories: ['string'],      // Apply to specific categories
    items: ['string'],           // Apply to specific items
    orderTypes: ['string'],      // Apply to specific order types
    dayOfWeek: ['number'],       // 0-6 (Sunday-Saturday)
    timeRange: { start: 'time', end: 'time' },
  },
  
  // Modifier
  modifier: {
    type: 'string',              // percentage, fixed
    value: 'number',             // e.g., 10 for 10% or 10 for +10 NOK
    direction: 'string',         // increase, decrease
  },
  
  // Priority
  priority: 'number',
  
  // Status
  isActive: 'boolean',
  effectiveFrom: 'date',
  effectiveTo: 'date',
  
  // Metadata
  createdAt: 'datetime',
  updatedAt: 'datetime',
};

// ============================================
// SAMPLE PRICING RULES
// ============================================

export const samplePricingRules = [
  {
    id: 'rule-001',
    name: 'Sweden Price Alignment',
    description: 'Swedish outlets use same base prices but in SEK',
    scope: { type: 'franchise', franchiseeId: 'isf-stockholm-ab' },
    ruleType: 'percentage_modifier',
    conditions: { categories: ['all'] },
    modifier: { type: 'percentage', value: 0, direction: 'none' },
    priority: 10,
    isActive: true,
  },
  {
    id: 'rule-002',
    name: 'Mall Location Surcharge',
    description: 'Götgatan (mall location) has 5% higher prices',
    scope: { type: 'outlet', outletId: 'se-gotgatan' },
    ruleType: 'percentage_modifier',
    conditions: { categories: ['all'] },
    modifier: { type: 'percentage', value: 5, direction: 'increase' },
    priority: 20,
    isActive: true,
  },
  {
    id: 'rule-003',
    name: 'Happy Hour Discount',
    description: '15% off beverages 15:00-17:00',
    scope: { type: 'global' },
    ruleType: 'percentage_modifier',
    conditions: { 
      categories: ['beverages'],
      timeRange: { start: '15:00', end: '17:00' },
    },
    modifier: { type: 'percentage', value: 15, direction: 'decrease' },
    priority: 5,
    isActive: false, // Not currently active
  },
];
